import {
	type Benchmark,
	getGetProjectNetworkStatsQueryOptions,
	getGetProjectQueryOptions,
	getListProjectBenchmarksQueryOptions,
	getListProjectDocumentsQueryOptions,
	getListProjectPersonsQueryOptions,
	getListProjectProductsQueryOptions,
	type Project,
	type ProjectDocument,
	type ProjectPerson,
	type ProjectProduct,
	useGetProjectSuspense,
} from '@amarantos/api-client'
import type { QueryClient } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import { ChevronRight, X } from 'lucide-react'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { RouteErrorFallback } from '@/components/route-error-fallback'
import { Button } from '@/components/ui/button'
import { ProjectBenchmarksPanel } from '@/features/benchmarks'
import { ensureConnectionEntryData, ensureNetworkEntryChoiceData } from '@/features/planning'
import { DefinitionNetworksStep } from '@/features/planning/components/definition-networks-step'
import { DefinitionAssetStep } from '@/features/projects/components/definition-asset-step'
import { DefinitionBasicDataEditStep } from '@/features/projects/components/definition-basic-data-step'
import { DefinitionBreakdownStep } from '@/features/projects/components/definition-breakdown-step'
import { DefinitionFlowChoiceStep } from '@/features/projects/components/definition-flow-choice-step'
import { DefinitionFlowBackButton, DefinitionFlowFrame } from '@/features/projects/components/definition-flow-frame'
import { DefinitionStepPlaceholder } from '@/features/projects/components/definition-step-placeholder'
import { ProjectDocumentsPanel } from '@/features/projects/components/project-documents-panel'
import { ProjectPersonsPanel } from '@/features/projects/components/project-persons-panel'
import { ProjectProductsPanel } from '@/features/projects/components/project-products-panel'
import {
	type DefinitionFlowAssetStep,
	getDefinitionFlowProgress,
	getNextDefinitionFlowStep,
	getPreviousDefinitionFlowStep,
	isDefinitionFlowAssetStep,
	resolveDefinitionFlowStep,
} from '@/features/projects/utils/definition-flow'
import { listAllProjectAssetsParams } from '@/features/projects/utils/project-asset-list'
import { projectDefinitionSearchSchema } from '@/features/projects/utils/search'

// An asset step embeds the tab view that reads one of these lists, so the loader warms it the way
// the tab route does — which also makes hovering "skip" preload the next step's list.
const prefetchAssetStepList = {
	documents: (queryClient, projectId) =>
		queryClient.ensureQueryData(getListProjectDocumentsQueryOptions(projectId, listAllProjectAssetsParams)),
	benchmarks: (queryClient, projectId) =>
		queryClient.ensureQueryData(getListProjectBenchmarksQueryOptions(projectId, listAllProjectAssetsParams)),
	materials: (queryClient, projectId) =>
		queryClient.ensureQueryData(getListProjectProductsQueryOptions(projectId, listAllProjectAssetsParams)),
	people: (queryClient, projectId) =>
		queryClient.ensureQueryData(getListProjectPersonsQueryOptions(projectId, listAllProjectAssetsParams)),
} as const satisfies Record<DefinitionFlowAssetStep, (queryClient: QueryClient, projectId: Project['id']) => void>

// The definition flow after its gate: basic data created the project, so every remaining step is
// project-scoped and skippable. The trailing underscore on `$projectId_` opts this route OUT of the
// `$projectId` layout (tab bar, breadcrumb, padded shell) — the flow is fullscreen, like the
// planning workspace. The URL stays `/projects/:id/definition`.
export const Route = createFileRoute('/_authenticated/projects/$projectId_/definition')({
	params: {
		parse: ({ projectId }) => ({ projectId: Number(projectId) }),
		stringify: ({ projectId }) => ({ projectId: String(projectId) }),
	},
	validateSearch: projectDefinitionSearchSchema,
	loaderDeps: ({ search: { step, networkId } }) => ({ step, networkId }),
	loader: ({ context: { queryClient }, params: { projectId }, deps: { step, networkId } }) => {
		queryClient.ensureQueryData(getGetProjectQueryOptions(projectId))
		// Warmed from the flow's start so the closing breakdown opens on numbers rather than a skeleton.
		queryClient.ensureQueryData(getGetProjectNetworkStatsQueryOptions(projectId))

		if (isDefinitionFlowAssetStep(step)) {
			prefetchAssetStepList[step](queryClient, projectId)
		}

		// The networks step opens on the method choice and reads the same list its forward button
		// does; a chosen network additionally warms the entry panel's own three queries.
		ensureNetworkEntryChoiceData(queryClient, projectId)
		if (networkId !== undefined) {
			ensureConnectionEntryData(queryClient, projectId, networkId)
		}
	},
	errorComponent: ({ error, reset }) => (
		<RouteErrorFallback error={error} reset={reset} fallbackMessageKey='error.load_project_details' />
	),
	component: ProjectDefinitionFlowRoute,
})

function ProjectDefinitionFlowRoute() {
	const { t } = useTranslation()
	const { projectId } = Route.useParams()
	const search = Route.useSearch()
	const navigate = Route.useNavigate()
	const project = useGetProjectSuspense(projectId).data

	const mode = search.mode
	const step = mode ? resolveDefinitionFlowStep(mode, search.step) : 'choice'
	const progress = mode ? getDefinitionFlowProgress(mode, step) : undefined
	const previousStep = mode ? getPreviousDefinitionFlowStep(mode, step) : undefined
	const nextStep = mode ? getNextDefinitionFlowStep(mode, step) : undefined

	// Everything entered so far is ordinary project data, so closing loses nothing — it lands on
	// the project tabs.
	const closeLink = { to: '/projects/$projectId/reports', params: { projectId } } as const

	const backButton = !!previousStep && <DefinitionFlowBackButton step={previousStep} />

	// The flow's closing action, wherever the registry ends: the breakdown step takes it directly, so
	// appending a step after it can never turn its button into a skip.
	const finishButton = (
		<Button asChild>
			<Link {...closeLink}>{t('projects.definition.finish')}</Link>
		</Button>
	)

	// One button, two readings: leaving a satisfied step is advancing, leaving one that still wants
	// something is skipping. Whether it is satisfied is the step's to say. The last step finishes.
	// `networkId` is scoped to the networks step, so moving on drops it (a no-op for every other).
	const renderForwardButton = (satisfied: boolean) =>
		nextStep ? (
			<Button asChild variant={satisfied ? 'default' : 'outline'}>
				<Link to='.' search={(previous) => ({ ...previous, step: nextStep, networkId: undefined })}>
					{t(satisfied ? 'projects.definition.next' : 'projects.definition.skip')}
					<ChevronRight />
				</Link>
			</Button>
		) : (
			finishButton
		)

	// The placeholder steps have nothing of their own to satisfy yet, so they always offer the skip.
	const skipButton = renderForwardButton(false)

	let stepContent: ReactNode
	if (step === 'choice') {
		stepContent = <DefinitionFlowChoiceStep selectedMode={mode} />
	} else if (step === 'basic-data') {
		stepContent = (
			<DefinitionBasicDataEditStep
				project={project}
				backButton={backButton}
				onSaved={() => {
					if (nextStep) {
						navigate({ search: (previous) => ({ ...previous, step: nextStep }) })
					}
				}}
			/>
		)
	} else if (step === 'networks') {
		stepContent = (
			<DefinitionNetworksStep
				projectId={projectId}
				networkId={search.networkId}
				onNetworkIdChange={(networkId) => {
					navigate({ search: (previous) => ({ ...previous, networkId }) })
				}}
				backButton={backButton}
				renderForwardButton={renderForwardButton}
			/>
		)
	} else if (step === 'documents') {
		stepContent = (
			<DefinitionAssetStep<ProjectDocument>
				step='documents'
				projectId={projectId}
				backButton={backButton}
				renderForwardButton={renderForwardButton}
			>
				{(assetDialog) => <ProjectDocumentsPanel projectId={projectId} {...assetDialog} />}
			</DefinitionAssetStep>
		)
	} else if (step === 'benchmarks') {
		stepContent = (
			<DefinitionAssetStep<Benchmark>
				step='benchmarks'
				projectId={projectId}
				backButton={backButton}
				renderForwardButton={renderForwardButton}
			>
				{(assetDialog) => <ProjectBenchmarksPanel projectId={projectId} {...assetDialog} />}
			</DefinitionAssetStep>
		)
	} else if (step === 'materials') {
		stepContent = (
			<DefinitionAssetStep<ProjectProduct>
				step='materials'
				projectId={projectId}
				backButton={backButton}
				renderForwardButton={renderForwardButton}
			>
				{(assetDialog) => <ProjectProductsPanel projectId={projectId} {...assetDialog} />}
			</DefinitionAssetStep>
		)
	} else if (step === 'people') {
		stepContent = (
			<DefinitionAssetStep<ProjectPerson>
				step='people'
				projectId={projectId}
				backButton={backButton}
				renderForwardButton={renderForwardButton}
			>
				{(assetDialog) => <ProjectPersonsPanel projectId={projectId} {...assetDialog} />}
			</DefinitionAssetStep>
		)
	} else if (step === 'breakdown') {
		stepContent = <DefinitionBreakdownStep projectId={projectId} backButton={backButton} finishButton={finishButton} />
	} else {
		stepContent = <DefinitionStepPlaceholder step={step} backButton={backButton} skipButton={skipButton} />
	}

	return (
		<DefinitionFlowFrame
			title={t('projects.definition.title')}
			subtitle={project.name}
			stepLabel={progress && t('projects.definition.progress', { current: progress.current, total: progress.total })}
			progress={progress}
			contentWidth={isDefinitionFlowAssetStep(step) ? 'wide' : 'default'}
			closeButton={
				<Button asChild variant='ghost' size='icon' aria-label={t('common.close')}>
					<Link {...closeLink}>
						<X />
					</Link>
				</Button>
			}
		>
			{stepContent}
		</DefinitionFlowFrame>
	)
}
